import { Alert, AlertTitle, Button } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { staticMenuItems } from '../config/menuConfig';
import { useAuth } from '../contexts/AuthContext';
import { isRouteAllowed } from '../domain/menuVisibility';

interface PageAccessGuardProps {
    children: ReactNode;
}

/**
 * Shows the page only to a user whose category lets them open it.
 *
 * The menu hides what a user may not open; this covers the other way in, an address typed into the
 * browser. It is a courtesy to the person, not the security boundary: what a restricted page loads
 * is still decided by the API.
 */
const PageAccessGuard = ({ children }: PageAccessGuardProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { pathname } = useLocation();

    const allowed = isRouteAllowed(
        pathname,
        { role: user?.role, allowedPages: user?.allowedPages },
        staticMenuItems,
    );

    if (allowed) {
        return <>{children}</>;
    }

    return (
        <Alert
            severity="warning"
            data-testid="page-access-denied"
            action={
                <Button component={RouterLink} to="/" color="inherit" size="small">
                    {t('permissions.noAccess.home')}
                </Button>
            }
        >
            <AlertTitle>{t('permissions.noAccess.title')}</AlertTitle>
            {t('permissions.noAccess.message')}
        </Alert>
    );
};

export default PageAccessGuard;
